import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Zoom,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Info,
  NetworkCheck,
  Refresh
} from '@mui/icons-material';
import { ipmiApi } from '../services/api';
import { IpmiInfo } from '../types';

// Props для компонента IPMI управления
interface IpmiManagementProps {
  serviceName: string;
  serverMonitoring: boolean; // Статус мониторинга сервера
}

const IpmiManagement: React.FC<IpmiManagementProps> = ({ serviceName, serverMonitoring }) => {
  const [ipmiStatus, setIpmiStatus] = useState<IpmiInfo | null>(null);
  const [html5Url, setHtml5Url] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [ipAddress, setIpAddress] = useState<string>('');
  const [detectingIp, setDetectingIp] = useState(false);

  const fetchIpmiStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ipmiApi.getIpmiStatus(serviceName);
      
      if (response.success) {
        setIpmiStatus(response.data);
      } else {
        setError(response.message || 'Ошибка при получении статуса IPMI');
      }
    } catch (err) {
      setError('Не удалось загрузить статус IPMI');
      console.error('Error fetching IPMI status:', err);
    } finally {
      setLoading(false);
    }
  };


  const fetchHtml5Url = async () => {
    try {
      setError(null);
      const response = await ipmiApi.getKvmHtml5Url(serviceName);

      if (response.success) {
        setHtml5Url(response.data);
      } else {
        setError(response.message || 'Не удалось получить HTML5 URL');
      }
    } catch (err) {
      setError('Ошибка при получении HTML5 URL');
      console.error('Error fetching HTML5 URL:', err);
    }
  };

  // Обработчик кнопки обновления - обновляет статус IPMI и HTML5 URL
  const handleRefresh = async () => {
    try {
      setError(null);

      // Обновляем статус IPMI (он сам управляет setLoading)
      await fetchIpmiStatus();

      // Пытаемся обновить HTML5 URL, если он уже был получен
      if (html5Url) {
        await fetchHtml5Url();
      }
    } catch (err) {
      console.error('Error refreshing:', err);
      setError('Ошибка при обновлении данных');
    }
  };

  const detectIpAddress = async () => {
    try {
      setDetectingIp(true);
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      setIpAddress(data.ip);
    } catch (err) {
      console.error('Error detecting IP:', err);
      setError('Не удалось автоматически определить IP-адрес');
    } finally {
      setDetectingIp(false);
    }
  };

  const handleRequestAccess = async () => {
    // Защита от двойного нажатия
    if (loading) {
      return;
    }

    // Проверка IP-адреса
    if (!ipAddress || !ipAddress.trim()) {
      setError('Пожалуйста, укажите IP-адрес для доступа');
      return;
    }

    // Валидация IP-адреса
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(ipAddress.trim())) {
      setError('Неверный формат IP-адреса');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Запрашиваем доступ с указанным IP
      const requestResponse = await ipmiApi.requestIpmiAccess(serviceName, ipAddress.trim());

      if (requestResponse.success) {
        setError(null);
        // Обновляем только HTML5 URL
        await fetchHtml5Url();
      } else {
        setError(requestResponse.message || 'Не удалось запросить доступ к IPMI');
      }
    } catch (err) {
      setError('Ошибка при запросе доступа к IPMI');
      console.error('Error requesting IPMI access:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIpmiStatus();
    detectIpAddress(); // Автоматически определяем IP при загрузке
  }, [serviceName]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !ipmiStatus) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button 
            variant="outlined" 
            onClick={fetchIpmiStatus}
            startIcon={<NetworkCheck />}
          >
            Повторить
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!ipmiStatus) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            IPMI недоступен для данного сервера
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            IPMI Управление
          </Typography>
          <Button
            variant="outlined"
            onClick={handleRefresh}
            startIcon={loading ? <CircularProgress size={16} /> : <NetworkCheck />}
            size="small"
            disabled={loading}
          >
            {loading ? 'Обновление...' : 'Обновить'}
          </Button>
        </Box>

        {/* Статус IPMI */}
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Статус IPMI
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              icon={ipmiStatus.available ? <CheckCircle /> : <Cancel />}
              label={ipmiStatus.available ? 'IPMI доступен' : 'IPMI недоступен'}
              color={ipmiStatus.available ? 'success' : 'error'}
              size="small"
            />
            <Chip
              icon={ipmiStatus.accessible ? <CheckCircle /> : <Cancel />}
              label={ipmiStatus.accessible ? 'Доступ активен' : 'Доступ неактивен'}
              color={ipmiStatus.accessible ? 'success' : 'warning'}
              size="small"
            />
          </Box>
        </Box>


        {/* Предупреждение о мониторинге */}
        {serverMonitoring && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>⚠️ ВНИМАНИЕ: Мониторинг сервера активен!</strong>
            </Typography>
            <Typography variant="body2">
              При установке ОС через KVM рекомендуется <strong>временно отключить мониторинг</strong> в настройках сервера.
              Иначе система мониторинга может обнаружить потерю связи во время установки и автоматически создать тикет в дата-центр
              или перезагрузить сервер.
            </Typography>
          </Alert>
        )}

        {/* KVM Access */}
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            KVM Доступ
          </Typography>

          {html5Url && html5Url.url ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                KVM готов к использованию! URL действителен до: {new Date(html5Url.expiration).toLocaleString('ru-RU')}
              </Alert>
              <Alert severity="info" sx={{ mb: 2 }}>
                ⚠️ Важно: Доступ к KVM ограничен по IP-адресу для безопасности. Убедитесь, что вы открываете KVM с того же IP-адреса, с которого был запрошен доступ.
              </Alert>
              <Zoom in={true} timeout={500}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<span>🖥️</span>}
                  onClick={() => window.open(html5Url.url, '_blank')}
                  sx={{
                    mb: 2,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
                    }
                  }}
                >
                  Открыть KVM
                </Button>
              </Zoom>
            </Box>
          ) : html5Url && html5Url.message ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                {html5Url.message}
              </Alert>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {html5Url.instructions}
              </Alert>
              <TextField
                fullWidth
                label="IP-адрес для доступа"
                value={ipAddress}
                onChange={(e) => setIpAddress(e.target.value)}
                placeholder="Введите IP-адрес или определите автоматически"
                helperText="Укажите IP-адрес, с которого вы будете открывать KVM"
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={detectIpAddress}
                        disabled={detectingIp}
                        edge="end"
                        title="Определить мой IP автоматически"
                      >
                        {detectingIp ? <CircularProgress size={20} /> : <Refresh />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Zoom in={true} timeout={300}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <span>⚡</span>}
                  onClick={handleRequestAccess}
                  disabled={loading}
                  sx={{
                    mb: 2,
                    transition: 'all 0.3s ease-in-out',
                    transform: loading ? 'scale(0.98)' : 'scale(1)',
                    '&:hover': {
                      transform: loading ? 'scale(0.98)' : 'scale(1.05)',
                    }
                  }}
                >
                  {loading ? 'Получение KVM URL...' : 'Запросить доступ к KVM'}
                </Button>
              </Zoom>
            </Box>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                KVM недоступен. Необходимо активировать IPMI доступ.
              </Alert>
              <TextField
                fullWidth
                label="IP-адрес для доступа"
                value={ipAddress}
                onChange={(e) => setIpAddress(e.target.value)}
                placeholder="Введите IP-адрес или определите автоматически"
                helperText="Укажите IP-адрес, с которого вы будете открывать KVM"
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={detectIpAddress}
                        disabled={detectingIp}
                        edge="end"
                        title="Определить мой IP автоматически"
                      >
                        {detectingIp ? <CircularProgress size={20} /> : <Refresh />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Zoom in={true} timeout={300}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <span>⚡</span>}
                  onClick={handleRequestAccess}
                  disabled={loading}
                  sx={{
                    mb: 2,
                    transition: 'all 0.3s ease-in-out',
                    transform: loading ? 'scale(0.98)' : 'scale(1)',
                    '&:hover': {
                      transform: loading ? 'scale(0.98)' : 'scale(1.05)',
                    }
                  }}
                >
                  {loading ? 'Получение KVM URL...' : 'Запросить доступ к KVM'}
                </Button>
              </Zoom>
            </Box>
          )}
        </Box>

        {/* Дополнительные действия */}
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            onClick={() => setDetailsOpen(true)}
            startIcon={<Info />}
          >
            Подробная информация
          </Button>

        </Box>

        {/* Диалог с подробной информацией */}
        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Подробная информация об IPMI</DialogTitle>
          <DialogContent>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color={ipmiStatus.available ? 'success' : 'error'} />
                </ListItemIcon>
                <ListItemText
                  primary="Доступность IPMI"
                  secondary={ipmiStatus.available ? 'Доступен' : 'Недоступен'}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color={ipmiStatus.accessible ? 'success' : 'warning'} />
                </ListItemIcon>
                <ListItemText
                  primary="Статус доступа"
                  secondary={ipmiStatus.accessible ? 'Активен' : 'Неактивен'}
                />
              </ListItem>

              {ipmiStatus.info && (
                <>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Активирован"
                      secondary={ipmiStatus.info.activated ? 'Да' : 'Нет'}
                    />
                  </ListItem>
                  
                  {ipmiStatus.info.supportedFeatures && (
                    <>
                      <Divider />
                      <ListItem>
                        <ListItemText
                          primary="Поддерживаемые функции"
                          secondary={
                            <Box component="div">
                              {Object.entries(ipmiStatus.info.supportedFeatures).map(([key, value]) => (
                                <Chip
                                  key={key}
                                  label={`${key}: ${value ? 'Да' : 'Нет'}`}
                                  size="small"
                                  color={value ? 'success' : 'default'}
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              ))}
                            </Box>
                          }
                        />
                      </ListItem>
                    </>
                  )}
                </>
              )}

              {ipmiStatus.access && (
                <>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Данные доступа"
                      secondary={JSON.stringify(ipmiStatus.access, null, 2)}
                    />
                  </ListItem>
                </>
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Закрыть</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default IpmiManagement;
